import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, Check, Plus, Trash2, AlertCircle, Package, 
  Calendar, Truck, TrendingUp, TrendingDown, AlertTriangle,
  Warehouse, Image, X
} from "lucide-react";
import IngredienteSearchSelect from "../../components/IngredienteSearchSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FatturaRevisione = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, localeCorrente, token } = useAuth();
  const [fattura, setFattura] = useState(null);
  const [ingredienti, setIngredienti] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Variazioni prezzo rilevate
  const [variazioniPrezzo, setVariazioniPrezzo] = useState({});
  
  // New ingredient dialog
  const [newIngredientDialog, setNewIngredientDialog] = useState(false);
  const [newIngredientIndex, setNewIngredientIndex] = useState(null);
  const [newIngredientForm, setNewIngredientForm] = useState({
    nome: "",
    categoria_id: "",
    unita_misura: "kg"
  });

  useEffect(() => {
    fetchData();
  }, [id, localeCorrente]);

  const fetchData = async () => {
    if (!localeCorrente) return;
    
    try {
      const headers = getAuthHeaders();
      const localeParam = `locale_id=${localeCorrente.id}`;
      
      // Prima ricalcola la fattura per aggiornare quantità e prezzi
      try {
        await axios.post(`${API}/fatture/${id}/recalculate`, {}, { headers });
      } catch {
        /* recalculate skipped silently — best-effort */
      }
      
      const [fattureRes, ingredientiRes, fornitoriRes, categorieRes] = await Promise.all([
        axios.get(`${API}/fatture?${localeParam}`, { headers }),
        axios.get(`${API}/ingredienti?${localeParam}`, { headers }),
        axios.get(`${API}/fornitori`, { headers }),
        axios.get(`${API}/categorie-ingredienti?${localeParam}`, { headers })
      ]);
      
      const found = fattureRes.data.find(f => f.id === id);
      if (!found) {
        toast.error("Fattura non trovata");
        navigate("/acquisti/storico");
        return;
      }
      
      // Aggiungi nome categoria agli ingredienti
      const categorieMap = {};
      categorieRes.data.forEach(c => { categorieMap[c.id] = c.nome; });
      const ingredientiConCategoria = ingredientiRes.data.map(ing => ({
        ...ing,
        categoria_nome: categorieMap[ing.categoria_id] || "Altro"
      }));
      
      setFattura(found);
      setIngredienti(ingredientiConCategoria);
      setFornitori(fornitoriRes.data);
      setCategorie(categorieRes.data);
      
      // Match automatico ingredienti per ogni riga
      if (found.righe) {
        matchIngredienti(found.righe, ingredientiRes.data, found.fornitore_id);
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  // Match automatico ingredienti
  const matchIngredienti = (righe, ingredientiList, fornitoreId) => {
    const variazioni = {};
    
    righe.forEach((riga, idx) => {
      if (riga.ingrediente_id) return; // Già matchato
      
      // Cerca ingrediente per nome (fuzzy match)
      const nomeRiga = (riga.prodotto_nome || "").toLowerCase().trim();
      const match = ingredientiList.find(ing => 
        ing.nome.toLowerCase().includes(nomeRiga) ||
        nomeRiga.includes(ing.nome.toLowerCase())
      );
      
      if (match) {
        riga.ingrediente_id = match.id;
        riga.ingrediente_nome = match.nome;
        
        // Verifica variazione prezzo
        const prezzoFattura = riga.prezzo_unitario || 0;
        const prezzoAttuale = match.prezzo_attuale || 0;
        
        if (prezzoAttuale > 0 && prezzoFattura > 0 && prezzoFattura !== prezzoAttuale) {
          const variazione = ((prezzoFattura - prezzoAttuale) / prezzoAttuale) * 100;
          variazioni[idx] = {
            precedente: prezzoAttuale,
            nuovo: prezzoFattura,
            percentuale: variazione
          };
        }
      }
    });
    
    setVariazioniPrezzo(variazioni);
  };

  const getFornitoreNome = (fornitoreId) => {
    const f = fornitori.find(f => f.id === fornitoreId);
    return f?.ragione_sociale || "Fornitore sconosciuto";
  };

  const updateRiga = (index, field, value) => {
    const newRighe = [...fattura.righe];
    newRighe[index] = { ...newRighe[index], [field]: value };
    
    // Ricalcola prezzo unitario se cambia prezzo_riga o quantita
    if (field === "prezzo_riga" || field === "quantita") {
      const prezzoRiga = field === "prezzo_riga" ? value : (newRighe[index].prezzo_riga || 0);
      const quantita = field === "quantita" ? value : (newRighe[index].quantita || 1);
      
      if (quantita > 0 && prezzoRiga > 0) {
        newRighe[index].prezzo_unitario = Math.round((prezzoRiga / quantita) * 100) / 100;
      }
      
      // Aggiorna variazione prezzo
      if (newRighe[index].ingrediente_id) {
        const ing = ingredienti.find(i => i.id === newRighe[index].ingrediente_id);
        if (ing && ing.prezzo_attuale > 0) {
          const prezzoNuovo = newRighe[index].prezzo_unitario || 0;
          if (prezzoNuovo > 0 && prezzoNuovo !== ing.prezzo_attuale) {
            const variazione = ((prezzoNuovo - ing.prezzo_attuale) / ing.prezzo_attuale) * 100;
            setVariazioniPrezzo(prev => ({
              ...prev,
              [index]: {
                precedente: ing.prezzo_attuale,
                nuovo: prezzoNuovo,
                percentuale: variazione
              }
            }));
          } else {
            setVariazioniPrezzo(prev => {
              const newVar = { ...prev };
              delete newVar[index];
              return newVar;
            });
          }
        }
      }
    }
    
    // Se cambia ingrediente_id, verifica prezzo
    if (field === "ingrediente_id" && value) {
      const ing = ingredienti.find(i => i.id === value);
      if (ing) {
        newRighe[index].ingrediente_nome = ing.nome;
        newRighe[index].unita_misura = ing.unita_misura;
        
        // Verifica variazione prezzo
        const prezzoFattura = newRighe[index].prezzo_unitario || 0;
        const prezzoAttuale = ing.prezzo_attuale || 0;
        
        if (prezzoAttuale > 0 && prezzoFattura > 0 && prezzoFattura !== prezzoAttuale) {
          const variazione = ((prezzoFattura - prezzoAttuale) / prezzoAttuale) * 100;
          setVariazioniPrezzo(prev => ({
            ...prev,
            [index]: {
              precedente: prezzoAttuale,
              nuovo: prezzoFattura,
              percentuale: variazione
            }
          }));
        } else {
          setVariazioniPrezzo(prev => {
            const newVar = { ...prev };
            delete newVar[index];
            return newVar;
          });
        }
      }
    }
    
    setFattura({ ...fattura, righe: newRighe });
  };

  const addRiga = () => {
    setFattura({
      ...fattura,
      righe: [...(fattura.righe || []), {
        ingrediente_id: null,
        ingrediente_nome: "",
        prodotto_nome: "",
        quantita: 1,
        unita_misura: "kg",
        prezzo_unitario: 0,
        confidence: 0
      }]
    });
  };

  const removeRiga = (index) => {
    const newRighe = fattura.righe.filter((_, i) => i !== index);
    setFattura({ ...fattura, righe: newRighe });
    setVariazioniPrezzo(prev => {
      const newVar = { ...prev };
      delete newVar[index];
      return newVar;
    });
  };

  const openNewIngredientDialog = (index, suggestedName) => {
    setNewIngredientIndex(index);
    setNewIngredientForm({
      nome: suggestedName || "",
      categoria_id: "",
      unita_misura: "kg"
    });
    setNewIngredientDialog(true);
  };

  const createNewIngredient = async () => {
    if (!newIngredientForm.nome.trim()) {
      toast.error("Inserisci il nome dell'ingrediente");
      return;
    }
    
    try {
      const riga = fattura.righe[newIngredientIndex];
      const payload = {
        locale_id: localeCorrente.id,
        nome: newIngredientForm.nome.trim(),
        categoria_id: newIngredientForm.categoria_id || null,
        unita_misura: newIngredientForm.unita_misura,
        prezzo_kg: riga?.prezzo_unitario || null,
        fornitore_id: fattura.fornitore_id
      };
      
      const res = await axios.post(`${API}/ingredienti`, payload, { headers: getAuthHeaders() });
      
      // Aggiorna lista ingredienti
      const newIngredienti = [...ingredienti, res.data];
      setIngredienti(newIngredienti);
      
      // Associa alla riga
      if (newIngredientIndex !== null) {
        updateRiga(newIngredientIndex, "ingrediente_id", res.data.id);
      }
      
      toast.success("Ingrediente creato e associato");
      setNewIngredientDialog(false);
      setNewIngredientIndex(null);
    } catch (error) {
      toast.error("Errore nella creazione ingrediente");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Salva fattura
      await axios.put(`${API}/fatture/${id}`, {
        data_fattura: fattura.data_fattura,
        fornitore_id: fattura.fornitore_id,
        righe: fattura.righe,
        stato: "confermata"
      }, { headers: getAuthHeaders() });
      
      // Calcola totale per storico
      const totale = fattura.righe.reduce((sum, r) => 
        sum + (r.prezzo_unitario || 0) * (r.quantita || 0), 0
      );
      
      // Aggiorna prezzi ingredienti e inventario per ogni riga
      for (const riga of fattura.righe) {
        if (riga.ingrediente_id && riga.prezzo_unitario > 0) {
          // Aggiorna prezzo con storico cronologico
          await axios.post(
            `${API}/ingredienti/${riga.ingrediente_id}/prezzo`,
            {
              ingrediente_id: riga.ingrediente_id,
              fornitore_id: fattura.fornitore_id,
              fornitore_nome: getFornitoreNome(fattura.fornitore_id),
              prezzo_kg: riga.prezzo_unitario,
              fattura_id: id,
              codice_fornitore: riga.codice_articolo || null,
              data_acquisto: fattura.data_fattura,
              fonte: "fattura"
            },
            { headers: getAuthHeaders() }
          );
          
          // Aggiorna inventario (carico)
          if (riga.quantita > 0) {
            await axios.post(
              `${API}/inventario/movimento?locale_id=${localeCorrente.id}`,
              {
                ingrediente_id: riga.ingrediente_id,
                quantita: riga.quantita, // Positivo = carico
                motivo: "fattura",
                riferimento_id: id
              },
              { headers: getAuthHeaders() }
            );
          }
        }
      }
      
      // Salva nello storico carichi
      try {
        await axios.post(
          `${API}/storico-carichi`,
          null,
          {
            headers: getAuthHeaders(),
            params: {
              locale_id: localeCorrente.id,
              tipo: "fattura",
              data: fattura.data_fattura,
              fornitore_id: fattura.fornitore_id,
              fornitore_nome: getFornitoreNome(fattura.fornitore_id),
              fattura_id: id,
              totale: totale,
              num_prodotti: fattura.righe.length,
              righe: JSON.stringify(fattura.righe.map(r => ({
                ingrediente_id: r.ingrediente_id,
                nome: r.ingrediente_nome || r.prodotto_nome,
                quantita: r.quantita,
                prezzo_unitario: r.prezzo_unitario,
                codice_articolo: r.codice_articolo
              })))
            }
          }
        );
      } catch {
        /* storico save skipped silently — best-effort */
      }
      
      toast.success("Fattura confermata e inventario aggiornato!");
      navigate("/acquisti/storico");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  if (!fattura) return null;

  const numVariazioni = Object.keys(variazioniPrezzo).length;
  const numNonMatchati = fattura.righe?.filter(r => !r.ingrediente_id).length || 0;
  
  // Funzione per aprire immagine fattura
  const openImageModal = async () => {
    if (fattura.immagine_path) {
      setImageUrl(`${process.env.REACT_APP_BACKEND_URL}${fattura.immagine_path}`);
      setShowImageModal(true);
    } else {
      toast.info("Immagine non disponibile per questa fattura");
    }
  };

  return (
    <div className="page-container space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/acquisti/storico")}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="page-title">Revisione Fattura</h1>
          <p className="text-muted-foreground text-sm">
            {getFornitoreNome(fattura.fornitore_id)}
          </p>
        </div>
        {/* Data Fattura - editabile */}
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <DateInput value={fattura.data_fattura || ""} onChange={(v) => setFattura({...fattura, data_fattura: v})} />
        </div>
        {/* Pulsante Vedi Foto */}
        <Button
          variant="outline"
          size="sm"
          onClick={openImageModal}
          className={fattura.immagine_path ? "text-blue-600 border-blue-300" : "text-muted-foreground"}
        >
          <Image className="h-4 w-4 mr-2" />
          Foto
        </Button>
      </div>

      {/* Alerts */}
      {!fattura.data_fattura && (
        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-start gap-3">
          <Calendar className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-600 dark:text-blue-400 font-medium">Data Fattura Mancante</p>
            <p className="text-blue-500/70 text-sm">
              Inserisci la data della fattura nel campo in alto a destra
            </p>
          </div>
        </div>
      )}

      {numVariazioni > 0 && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-600 dark:text-amber-400 font-medium">Variazioni Prezzo Rilevate</p>
            <p className="text-amber-400/70 text-sm">
              {numVariazioni} ingredient{numVariazioni === 1 ? 'e' : 'i'} con prezzo diverso dal precedente
            </p>
          </div>
        </div>
      )}

      {numNonMatchati > 0 && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-600 dark:text-red-400 font-medium">Ingredienti Non Trovati</p>
            <p className="text-red-500/70 text-sm">
              {numNonMatchati} prodott{numNonMatchati === 1 ? 'o' : 'i'} da associare o creare
            </p>
          </div>
        </div>
      )}

      {/* Righe Fattura */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="section-title">Prodotti ({fattura.righe?.length || 0})</h2>
          <Button variant="outline" size="sm" onClick={addRiga}>
            <Plus className="h-4 w-4 mr-1" /> Aggiungi
          </Button>
        </div>

        {fattura.righe?.map((riga, idx) => {
          const variazione = variazioniPrezzo[idx];
          const isMatched = !!riga.ingrediente_id;
          
          return (
            <div 
              key={idx} 
              className={`p-4 rounded-xl border ${
                !isMatched 
                  ? 'bg-red-50 dark:bg-red-500/10 border-red-300 dark:border-red-500/30' 
                  : variazione 
                    ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/30'
                    : 'bg-muted/30 border-border'
              }`}
            >
              {/* Layout Mobile-First */}
              <div className="space-y-3">
                {/* Header riga: Prodotto OCR + Delete */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{riga.prodotto_nome || "Prodotto"}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5">
                      {riga.codice_articolo && (
                        <span className="text-xs text-muted-foreground">Cod: {riga.codice_articolo}</span>
                      )}
                      {riga.formato_peso && (
                        <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                          {riga.formato_peso}
                        </span>
                      )}
                      {riga.um && (
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          riga.um === 'CT' 
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' 
                            : 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400'
                        }`}>
                          {riga.quantita_fattura || riga.quantita} {riga.um}
                        </span>
                      )}
                      {riga.prezzo_riga > 0 && (
                        <span className="text-xs text-muted-foreground">
                          Tot: €{riga.prezzo_riga?.toFixed(2)}
                        </span>
                      )}
                      {riga.match_method && (
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                          riga.match_method === 'codice' 
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' 
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                        }`}>
                          {riga.match_method === 'codice' ? 'Codice' : 'Nome'}
                        </span>
                      )}
                    </div>
                    {/* Mostra calcolo quantità se CT/PZ */}
                    {riga.um && riga.formato_peso && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                        → {riga.quantita?.toFixed(3)} kg calcolati ({riga.quantita_fattura} {riga.um} × {riga.formato_peso})
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRiga(idx)}
                    className="h-8 w-8 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 flex-shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Ingrediente con ricerca */}
                <div>
                  <Label className="text-muted-foreground text-xs mb-1 block">Ingrediente *</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <IngredienteSearchSelect
                        ingredienti={ingredienti}
                        categorie={categorie}
                        value={riga.ingrediente_id || ""}
                        onChange={(v) => updateRiga(idx, "ingrediente_id", v)}
                        placeholder="Cerca ingrediente..."
                        hasError={!isMatched}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openNewIngredientDialog(idx, riga.prodotto_nome)}
                      className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 flex-shrink-0 h-10 w-10"
                      title="Crea nuovo ingrediente"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Codice + Tot Riga + Quantità + Prezzo in griglia */}
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-muted-foreground text-xs mb-1 block">Codice</Label>
                    <Input
                      value={riga.codice_articolo || ""}
                      onChange={(e) => updateRiga(idx, "codice_articolo", e.target.value)}
                      placeholder="ART001"
                      className="text-sm h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs mb-1 block">Tot €</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={riga.prezzo_riga || ""}
                      onChange={(e) => updateRiga(idx, "prezzo_riga", parseFloat(e.target.value) || 0)}
                      className="text-sm h-10 bg-amber-50 dark:bg-amber-500/10"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs mb-1 block">
                      Qta ({riga.unita_misura || "kg"})
                    </Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={riga.quantita || ""}
                      onChange={(e) => updateRiga(idx, "quantita", parseFloat(e.target.value) || 0)}
                      className="text-sm h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs mb-1 block">
                      €/{riga.unita_misura || "kg"}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={riga.prezzo_unitario || ""}
                      onChange={(e) => updateRiga(idx, "prezzo_unitario", parseFloat(e.target.value) || 0)}
                      className={`text-sm h-10 font-medium ${variazione ? 'border-amber-500' : ''}`}
                      readOnly
                    />
                  </div>
                </div>

                {/* Prezzo precedente e variazione */}
                {(() => {
                  const ing = riga.ingrediente_id ? ingredienti.find(i => i.id === riga.ingrediente_id) : null;
                  const prezzoPrecedente = ing?.prezzo_attuale;
                  const showVariazione = variazione && variazione.percentuale !== 0;
                  
                  if (!prezzoPrecedente && !showVariazione) return null;
                  
                  return (
                    <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm">
                      {prezzoPrecedente > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Ultimo prezzo:</span>
                          <span className="font-medium">€{prezzoPrecedente.toFixed(2)}/{riga.unita_misura || 'kg'}</span>
                        </div>
                      )}
                      {showVariazione && (
                        <div className={`flex items-center gap-1 font-medium ${variazione.percentuale > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                          {variazione.percentuale > 0 ? (
                            <TrendingUp className="h-4 w-4" />
                          ) : (
                            <TrendingDown className="h-4 w-4" />
                          )}
                          <span>{variazione.percentuale > 0 ? '+' : ''}{variazione.percentuale.toFixed(1)}%</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Inventario */}
      <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 rounded-xl flex items-start gap-3">
        <Warehouse className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-emerald-700 dark:text-emerald-400 font-medium">Aggiornamento Inventario</p>
          <p className="text-emerald-600/70 dark:text-emerald-400/70 text-sm">
            Confermando, le quantità verranno caricate in magazzino e i prezzi aggiornati
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => navigate("/acquisti/storico")}
          className="flex-1"
        >
          Annulla
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || numNonMatchati > 0}
          className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold disabled:opacity-50"
        >
          {saving ? "Salvataggio..." : (
            <>
              <Check className="h-4 w-4 mr-2" />
              Conferma e Carica
            </>
          )}
        </Button>
      </div>

      {/* Dialog Nuovo Ingrediente */}
      <Dialog open={newIngredientDialog} onOpenChange={setNewIngredientDialog}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-600" />
              Nuovo Ingrediente
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={newIngredientForm.nome}
                onChange={(e) => setNewIngredientForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="es. Mozzarella Fior di Latte"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select
                  value={newIngredientForm.categoria_id}
                  onValueChange={(v) => setNewIngredientForm(prev => ({ ...prev, categoria_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categorie.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unità</Label>
                <Select
                  value={newIngredientForm.unita_misura}
                  onValueChange={(v) => setNewIngredientForm(prev => ({ ...prev, unita_misura: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">Kg (solidi)</SelectItem>
                    <SelectItem value="litro">Litro (liquidi)</SelectItem>
                    <SelectItem value="pz">Pezzi (unità)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {newIngredientIndex !== null && fattura.righe[newIngredientIndex] && (
              <div className="p-3 bg-muted rounded-xl text-sm">
                <p className="text-muted-foreground">Il prezzo €{fattura.righe[newIngredientIndex].prezzo_unitario?.toFixed(2) || '0.00'}/{newIngredientForm.unita_misura} e il fornitore verranno salvati automaticamente</p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNewIngredientDialog(false)} className="flex-1">
              Annulla
            </Button>
            <Button onClick={createNewIngredient} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
              Crea e Associa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Immagine Fattura */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 z-10 bg-black/50 text-white hover:bg-black/70 rounded-full"
            >
              <X className="h-5 w-5" />
            </Button>
            {imageUrl && (
              <img 
                src={imageUrl} 
                alt="Fattura" 
                className="w-full h-auto max-h-[85vh] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FatturaRevisione;
